import {DateTime, Interval} from "luxon";
import type {RichTextDocument} from "@o2d/rich-text-editor";
import {inject, singleton} from "tsyringe";
import {ICalEvent} from "../domain/dtos/i-cal-event.dto";
import {IssueStatus} from "../domain/entities/issue-status.entity";
import {IssueType} from "../domain/entities/issue-type.entity";
import {Issue} from "../domain/entities/issue.entity";
import {Project} from "../domain/entities/project.entity";
import {IssueHelper} from "../domain/helpers/issue.helper";
import {IssueStatusRepository} from "../domain/repositories/issue-status.repository";
import {IssueTypeRepository} from "../domain/repositories/issue-type.repository";
import {ProjectHelper} from "../helpers/project.helper";
import {ShortIdHelper} from "../helpers/short-id.helper";
import {AbstractIcalToEntityTransformer} from "./abstract-ical-to-entity.transformer";

@singleton()
export class ICalToIssueTransformer extends AbstractIcalToEntityTransformer<Issue | null> {

    constructor(
        @inject(IssueTypeRepository) private readonly issueTypeRepository: IssueTypeRepository,
        @inject(IssueStatusRepository) private readonly issueStatusRepository: IssueStatusRepository
    ) {
        super();
    }

    public async transform(iCalEvent: ICalEvent, projects: Map<string, Project>): Promise<Issue | null> {


        const projectKey = ProjectHelper.getTitle(iCalEvent).toLocaleLowerCase();
        if (!projects.has(projectKey)) {
            console.error(`No project found for "${iCalEvent.summary}" with key "${projectKey}"`);
        }

        const project = projects.get(projectKey);
        if (!project) {
            return null;
        }

        const type = await this._findIssueType(iCalEvent);
        const status = await this._findIssueStatus(iCalEvent);
        const issue = new Issue(type, status);

        issue.uid = ShortIdHelper.generate();
        issue.title = IssueHelper.getTitle(iCalEvent);
        issue.timeRange = Interval.fromDateTimes(
            DateTime.fromJSDate(iCalEvent.start).startOf(`day`),
            DateTime.fromJSDate(iCalEvent.end).startOf(`day`)
        );
        issue.description = this._transformToRichTextDocument(iCalEvent.description || '');

        if (iCalEvent.attendee) {
            issue.employees = await this._getEmployees(iCalEvent.attendee);
        }

        if (iCalEvent.organizer) {
            const creator = await this._getCreator(iCalEvent.organizer);
            if (creator) {
                issue.creator = creator;
            }
        }

        return issue;
    }

    protected async _findIssueType(iCalEvent: ICalEvent): Promise<IssueType> {

        const issueTypeDescription = iCalEvent.summary.trim().split(`:`)?.shift()?.split(' ')?.pop();
        if (!issueTypeDescription) {
            return (await this.issueTypeRepository.findOneBy(`include`, `unknown`))!;
        }

        return (await this.issueTypeRepository.findOneBy(`description`, issueTypeDescription)) ?? (await this.issueTypeRepository.findOneBy(`include`, `unknown`))!;
    }

    private async _findIssueStatus(iCalEvent: ICalEvent): Promise<IssueStatus> {
        const statusOfICal = (iCalEvent.location || 'offen').trim().toLocaleLowerCase();
        const status = await this.issueStatusRepository.findOneBy(`key`, statusOfICal === `erledigt` ? `DONE` : `OPEN`)
        if (!status) {
            throw Error(`Could not find issue status "${statusOfICal}"`);
        }

        return status;
    }

    /**
     * A plain-text iCal description, one paragraph per line — built directly
     * as a `RichTextDocument` (no formatting to carry, so there is nothing a
     * live editor instance would add) rather than round-tripping through a
     * throwaway Quill instance the way this used to, before the RTE
     * unification (2026-09) made `RichTextDocument` the field's native shape.
     */
    private _transformToRichTextDocument(value: string): RichTextDocument {
        const lines = value.split('\n');
        return {
            type: 'doc',
            content: lines.map(line => ({
                type: 'paragraph',
                ...(line.length > 0 ? {content: [{type: 'text', text: line}]} : {}),
            })),
        };
    }
}